if (!customElements.get("color-swatch")) {
  class ColorSwatch extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.optionNodes = this.querySelectorAll("[data-value]");
      this.init();
    }

    disconnectedCallback() {
      this.optionNodes.forEach((button) =>
        button.removeEventListener("mouseenter", this.onMouseEnter.bind(this))
      );
    }

    init() {
      const productCard = this.closest(".product-card");
      this.mainImage = productCard?.querySelector(
        ".product-card__image--main img"
      );
      this.optionNodes.forEach((button) =>
        button.addEventListener("mouseenter", this.onMouseEnter.bind(this))
      );

      this.selected = this.querySelector('a[aria-selected="true"]');

      this.getVariantData();
      this.initOptionSwatches();
    }

    initOptionSwatches() {
      this.optionNodes.forEach((optNode) => {
        let variantImage;
        const { optionPosition, value: optionValue } = optNode.dataset;
        const variant = this.variantData.find(
          (v) =>
            v[`option${optionPosition}`] &&
            v[`option${optionPosition}`].toLowerCase() ===
              optionValue.toLowerCase()
        );
        if (variant) {
          variantImage = variant.featured_image
            ? variant.featured_image.src
            : "";
          optNode.href = `${optNode.href}?variant=${variant.id}`;
          if (variantImage) {
            optNode.setAttribute(
              "data-src",
              getSizedImageUrl(variantImage, "900x")
            );
            optNode.setAttribute("data-srcset", getSrcset(variantImage));
          }
        }
      });
    }

    onMouseEnter(e) {
      const { target } = e;
      this.selected && this.selected.removeAttribute("aria-selected");
      target.setAttribute("aria-selected", true);

      const { src, srcset } = target.dataset;
      if (this.mainImage && srcset) {
        this.mainImage.src = src;
        this.mainImage.srcset = srcset;
      }

      this.selected = target;
    }

    getVariantData() {
      this.variantData =
        this.variantData || JSON.parse(this.nextElementSibling?.textContent || "[]");
    }
  }
  customElements.define("color-swatch", ColorSwatch);
}

(() => {
  if (window.PeakStyleCrossSellGuard) return;
  window.PeakStyleCrossSellGuard = true;

  const norm = (value = "") => value.toString().trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  const audienceFromText = (value = "") => {
    const text = norm(value);
    if (/\b(damen|frauen|women|woman)\b/.test(text) || /\bscott\s+w\b/.test(text)) return "damen";
    if (/\b(herren|manner|maenner|men|man)\b/.test(text) || /\bscott\s+m\b/.test(text)) return "herren";
    if (/\b(madchen|maedchen|girls?)\b/.test(text)) return "madchen";
    if (/\b(jungen|boys?)\b/.test(text)) return "jungen";
    if (/\b(kinder|kids?|youth)\b/.test(text)) return "kinder";
    return "unisex";
  };

  const compatibleAudience = (source, candidate) => {
    if (!candidate) return false;
    switch (source) {
      case "herren": return candidate === "herren" || candidate === "unisex";
      case "damen": return candidate === "damen" || candidate === "unisex";
      case "jungen": return candidate === "jungen" || candidate === "kinder";
      case "madchen": return candidate === "madchen" || candidate === "kinder";
      case "kinder": return ["jungen", "madchen", "kinder"].includes(candidate);
      default: return ["herren", "damen", "unisex"].includes(candidate);
    }
  };

  const colorFamilies = (value = "") => {
    const text = norm(value);
    const families = new Set();
    const tests = [
      ["black", /black|schwarz|night|onyx|graphite/],
      ["white", /white|weiss|weis|snow|ivory|cotton/],
      ["grey", /grey|gray|grau|anthracite|slate|stone|silver|spray/],
      ["blue", /blue|blau|navy|aqua|turquoise|turkis|ocean|marine|denim|azure/],
      ["green", /green|grun|gruen|olive|moss|forest|lime|khaki/],
      ["red", /red|rot|burgundy|crimson|wine/],
      ["orange", /orange|coral|apricot|gleam|mandarin|rust/],
      ["yellow", /yellow|gelb|gold|mustard/],
      ["pink", /pink|rosa|rose|magenta|fuchsia/],
      ["purple", /purple|violet|lila|lavender|plum/],
      ["brown", /brown|braun|beige|sand|tan|taupe|camel|toast/]
    ];
    tests.forEach(([family, re]) => { if (re.test(text)) families.add(family); });
    return families;
  };

  const selectedSourceColor = () => {
    const fields = Array.from(document.querySelectorAll('product-info variant-picker [data-picker-field]'));
    const colorField = fields.find((field) => {
      const name = norm(field.dataset.optionName || "");
      return name.includes("farbe") || name.includes("color") || name.includes("colour");
    });
    if (!colorField) return "";
    const select = colorField.querySelector("select");
    if (select) return select.value || "";
    return colorField.querySelector('input[type="radio"]:checked')?.value || "";
  };

  const sourceAudience = () => {
    const h1 = document.querySelector('product-info h1, .f-product-single__title, h1');
    return audienceFromText(`${h1?.textContent || ""} ${location.pathname}`);
  };

  const cardAudience = (card) => {
    const marker = card.querySelector('[data-peakstyle-card-audience]');
    const explicit = norm(marker?.dataset.peakstyleCardAudience || "");
    if (explicit) return explicit;
    return audienceFromText(card.querySelector('.product-card__title')?.textContent || "");
  };

  const cardColorVariants = (card) => {
    const swatch = card.querySelector('color-swatch');
    if (!swatch) return [];
    let variants = [];
    try {
      variants = swatch.variantData || JSON.parse(swatch.nextElementSibling?.textContent || "[]");
    } catch (_) {
      return [];
    }
    const optionNode = swatch.querySelector('[data-option-position]');
    const position = Number(optionNode?.dataset.optionPosition || 0);
    if (!position) return [];
    return variants.filter((variant) => variant.available !== false && variant[`option${position}`]).map((variant) => ({
      variant,
      color: variant[`option${position}`],
      families: colorFamilies(variant[`option${position}`])
    }));
  };

  const bestColorMatch = (card, sourceColor) => {
    const sourceFamilies = colorFamilies(sourceColor);
    const variants = cardColorVariants(card);
    let best = null;
    let bestScore = -1;
    variants.forEach((item) => {
      let score = 0;
      if (norm(item.color) === norm(sourceColor)) score += 100;
      item.families.forEach((family) => {
        if (sourceFamilies.has(family)) score += 30;
        else if (["black", "white", "grey"].includes(family)) score += 3;
      });
      if (sourceFamilies.size === 0 && item.families.size > 0) score += 1;
      if (score > bestScore) {
        bestScore = score;
        best = item;
      }
    });
    return { best, score: Math.max(bestScore, 0) };
  };

  const applyVariantToCard = (card, match) => {
    const item = match?.best;
    if (!item?.variant) return;
    const variant = item.variant;
    const swatch = card.querySelector('color-swatch');
    const option = swatch && Array.from(swatch.querySelectorAll('[data-value]')).find((node) => norm(node.dataset.value) === norm(item.color));
    if (option) {
      option.dispatchEvent(new MouseEvent('mouseenter', { bubbles: true }));
    } else if (variant.featured_image?.src) {
      const image = card.querySelector('.product-card__image--main img');
      if (image) {
        image.src = typeof getSizedImageUrl === "function" ? getSizedImageUrl(variant.featured_image.src, "900x") : variant.featured_image.src;
        if (typeof getSrcset === "function") image.srcset = getSrcset(variant.featured_image.src);
      }
    }
    card.querySelectorAll('a.product-card__link, a.full-width-link').forEach((link) => {
      const url = new URL(link.href, location.origin);
      url.searchParams.set('variant', variant.id);
      link.href = url.toString();
    });
  };

  const getColumns = (container) => {
    const seen = new Set();
    return Array.from(container.querySelectorAll('.f-column')).filter((column) => {
      const card = column.querySelector(':scope > .product-card, .product-card');
      if (!card || seen.has(card)) return false;
      seen.add(card);
      return true;
    });
  };

  const processBlock = (block) => {
    const container = block.querySelector('[data-products-container]');
    if (!container) {
      block.dataset.peakstyleCrossSellReady = "true";
      return;
    }

    const source = sourceAudience();
    const sourceColor = selectedSourceColor();
    const columns = getColumns(container);
    const ranked = [];

    columns.forEach((column, index) => {
      const card = column.querySelector('.product-card');
      if (!card) return;
      const audience = cardAudience(card);
      if (!compatibleAudience(source, audience)) {
        column.remove();
        return;
      }
      const match = bestColorMatch(card, sourceColor);
      applyVariantToCard(card, match);
      ranked.push({ column, score: match.score, index });
    });

    const parentGroups = new Map();
    ranked.forEach((item) => {
      const parent = item.column.parentElement;
      if (!parentGroups.has(parent)) parentGroups.set(parent, []);
      parentGroups.get(parent).push(item);
    });

    parentGroups.forEach((items, parent) => {
      const desiredOrder = [...items].sort((a, b) => b.score - a.score || a.index - b.index);
      desiredOrder.forEach(({ column }) => parent.appendChild(column));
    });

    block.dataset.peakstyleCrossSellReady = "true";
    window.dispatchEvent(new Event('resize'));
  };

  const processAll = (force = false) => {
    document.querySelectorAll('.template-product .product-recommendations').forEach((block) => {
      if (force || block.dataset.peakstyleCrossSellReady !== "true") processBlock(block);
    });
  };

  const style = document.createElement('style');
  style.textContent = '.template-product .product-recommendations:not([data-peakstyle-cross-sell-ready="true"]){visibility:hidden}';
  document.head.appendChild(style);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => processAll(false), { once: true });
  else processAll(false);

  document.addEventListener('variant:changed', () => window.setTimeout(() => processAll(true), 0));
  document.addEventListener('change', (event) => {
    if (event.target.closest?.('product-info variant-picker')) window.setTimeout(() => processAll(true), 0);
  });

  new MutationObserver(() => processAll(false)).observe(document.documentElement, { childList: true, subtree: true });
})();
